import type { UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import type { TaskManagementTaskDetail } from '../../data-contract/task-management';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { isPendingGenerationState } from '../hooks/use-is-generating';
import {
    isAutomationAssessmentPending,
    mapProfileTaskAutomation,
} from '../profile-tasks/api/profile-task-automation-adapter';
import { jobArchitectureKeys } from './query-keys';

type UseTaskManagementTaskOptions = {
    readonly enabled?: boolean;
    /** Reads the task again while it is being generated, so its content shows when generation finishes. */
    readonly followGeneration?: boolean;
    /** Reads the title and content in this language; the browser's language decides when omitted. */
    readonly language?: LanguageCodeForJSA;
    readonly taskId: string;
    /** Pins the read to one task version; the latest version is read when omitted. */
    readonly version?: number;
};

const ASSESSMENT_REFETCH_INTERVAL_MS = 5000;
const GENERATION_REFETCH_INTERVAL_MS = 2000;
const STALE_TIME_MS = 10_000;

/** Reads the task as `useTaskManagementTask` caches it. */
const readTask =
    (
        fetchTaskManagementTask: ReturnType<typeof useApplicationFetchers>['fetchTaskManagementTask'],
        { language, taskId, version }: Omit<UseTaskManagementTaskOptions, 'enabled'>,
    ) =>
    async (): Promise<TaskManagementTaskDetail> => {
        const result = await fetchTaskManagementTask({ acceptLanguage: language, taskId, version });

        if (result.isErr === true) {
            throw result.error;
        }

        return result.value;
    };

/** While the task's assessment is queued or running, the task is read again so the result shows when it lands. */
export const taskManagementTaskRefetchInterval = (task: TaskManagementTaskDetail | undefined): number | false =>
    task !== undefined && isAutomationAssessmentPending(mapProfileTaskAutomation(task))
        ? ASSESSMENT_REFETCH_INTERVAL_MS
        : false;

/** While a followed task is queued or being generated, it is read again until its content lands. */
const generationRefetchInterval = (
    task: TaskManagementTaskDetail | undefined,
    otherwise: number | false,
): number | false =>
    task !== undefined && isPendingGenerationState(task.generationState) ? GENERATION_REFETCH_INTERVAL_MS : otherwise;

export const useTaskManagementTask = ({
    enabled = true,
    followGeneration = false,
    language,
    taskId,
    version,
}: UseTaskManagementTaskOptions): UseQueryResult<TaskManagementTaskDetail, Error> => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const { fetchTaskManagementTask } = useApplicationFetchers();
    // Only the flagged task tile shows the assessment, so only it waits for a pending one.
    const followsAssessment = featureFlagService.isFeatureFlagEnabled('task-automation-recipes');

    return useQuery({
        queryFn: readTask(fetchTaskManagementTask, { language, taskId, version }),
        enabled,
        queryKey: jobArchitectureKeys.taskManagementTask(taskId, version, language),
        refetchInterval: (query) => {
            const assessment = followsAssessment ? taskManagementTaskRefetchInterval(query.state.data) : false;

            return followGeneration ? generationRefetchInterval(query.state.data, assessment) : assessment;
        },
        staleTime: STALE_TIME_MS,
        throwOnError: false,
    });
};

/** Reads several pinned task versions at once, e.g. every task of a profile; each result keeps its task's place. */
export const useTaskManagementTasks = ({
    followGeneration = false,
    language,
    tasks,
}: {
    /** Reads each task again while it is being generated, like `useTaskManagementTask`. */
    readonly followGeneration?: boolean;
    readonly language?: LanguageCodeForJSA;
    readonly tasks: readonly { readonly taskId: string; readonly version: number }[];
}): UseQueryResult<TaskManagementTaskDetail, Error>[] => {
    const { fetchTaskManagementTask } = useApplicationFetchers();

    return useQueries({
        queries: tasks.map(({ taskId, version }) =>
            queryOptions({
                queryFn: readTask(fetchTaskManagementTask, { language, taskId, version }),
                queryKey: jobArchitectureKeys.taskManagementTask(taskId, version, language),
                refetchInterval: (query) => {
                    const assessment = taskManagementTaskRefetchInterval(query.state.data);

                    return followGeneration ? generationRefetchInterval(query.state.data, assessment) : assessment;
                },
                staleTime: STALE_TIME_MS,
                throwOnError: false,
            }),
        ),
    });
};

/** Reads a task ahead of its tile, as the person reaches for it, so the tile opens with the task already shown. */
export const usePrefetchTaskManagementTask = (): ((options: Omit<UseTaskManagementTaskOptions, 'enabled'>) => void) => {
    const queryClient = useQueryClient();
    const { fetchTaskManagementTask } = useApplicationFetchers();

    return ({ language, taskId, version }) => {
        void queryClient.prefetchQuery({
            queryFn: readTask(fetchTaskManagementTask, { language, taskId, version }),
            queryKey: jobArchitectureKeys.taskManagementTask(taskId, version, language),
            staleTime: STALE_TIME_MS,
        });
    };
};
